import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  User,
  Phone,
  MapPin,
  Heart,
  Brain,
  Home,
  Activity,
  Stethoscope,
  Eye,
  Ear,
  MessageSquare,
  Save,
  Send,
  Mic,
  MicOff,
  Sparkles,
  Volume2,
  Square,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Voice-Powered Assessment Form
 * 
 * Step 3 of the Intake Workflow
 * Digital version of ATOA's Client Assessment Form
 * 
 * Features:
 * - Full Voice Assessment Mode: Record entire assessment, AI extracts all fields
 * - Field-by-field voice input: Mic button on every field
 * - Mobile-optimized for home visits
 * 
 * Sections:
 * 1. Client Demographics & Contacts
 * 2. Vital Signs
 * 3. Medical History
 * 4. Dental/Vision/Hearing
 * 5. Mental/Cognitive Assessment
 * 6. ADL Assessment
 * 7. IADL Assessment
 * 8. Living Habits & Social Profile
 */

const SECTIONS = [
  { key: 'voice', label: 'Voice Mode', icon: Mic, mobileLabel: 'Voice' },
  { key: 'demographics', label: 'Demographics', icon: User, mobileLabel: 'Demo' },
  { key: 'vital_signs', label: 'Vital Signs', icon: Activity, mobileLabel: 'Vitals' },
  { key: 'medical', label: 'Medical History', icon: Stethoscope, mobileLabel: 'Medical' },
  { key: 'senses', label: 'Vision/Hearing', icon: Eye, mobileLabel: 'Senses' },
  { key: 'mental', label: 'Mental Status', icon: Brain, mobileLabel: 'Mental' },
  { key: 'adl', label: 'ADL Assessment', icon: Heart, mobileLabel: 'ADL' },
  { key: 'iadl', label: 'IADL Assessment', icon: Home, mobileLabel: 'IADL' },
  { key: 'summary', label: 'Summary', icon: ClipboardCheck, mobileLabel: 'Review' },
];

const ADL_ITEMS = [
  { key: 'bathing', label: 'Bathing', description: 'Showering, tub bathing, sponge bathing' },
  { key: 'dressing', label: 'Dressing', description: 'Putting on and taking off clothes' },
  { key: 'grooming', label: 'Grooming', description: 'Hair care, shaving, nail care, oral hygiene' },
  { key: 'toileting', label: 'Toileting', description: 'Using toilet, bedpan, commode' },
  { key: 'transferring', label: 'Transferring', description: 'Moving in/out of bed, chair, wheelchair' },
  { key: 'eating', label: 'Eating', description: 'Feeding oneself' },
  { key: 'ambulation', label: 'Ambulation', description: 'Walking, using mobility aids' },
];

const IADL_ITEMS = [
  { key: 'meal_preparation', label: 'Meal Preparation', description: 'Planning and cooking meals' },
  { key: 'housekeeping', label: 'Housekeeping', description: 'Light cleaning, tidying' },
  { key: 'laundry', label: 'Laundry', description: 'Washing, drying, folding clothes' },
  { key: 'shopping', label: 'Shopping', description: 'Grocery and essential shopping' },
  { key: 'transportation', label: 'Transportation', description: 'Driving or arranging transport' },
  { key: 'medication_management', label: 'Medication Management', description: 'Taking medications as prescribed' },
  { key: 'telephone', label: 'Telephone Use', description: 'Making and receiving calls' },
  { key: 'finances', label: 'Finances', description: 'Managing bills, banking' },
];

const ASSISTANCE_LEVELS = [
  { value: 'independent', label: 'Independent', short: 'Indep', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'supervision', label: 'Supervision', short: 'Super', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'minimal', label: 'Minimal', short: 'Min', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'moderate', label: 'Moderate', short: 'Mod', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'maximal', label: 'Maximal', short: 'Max', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'dependent', label: 'Dependent', short: 'Dep', color: 'bg-red-200 text-red-800 border-red-300' },
];

export default function AssessmentForm() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isFullVoiceMode, setIsFullVoiceMode] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [activeVoiceField, setActiveVoiceField] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    // Demographics
    client_name: '',
    date_of_birth: '',
    client_phone: '',
    client_address: '',
    doctor_name: '',
    doctor_phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    
    // Vital Signs
    blood_pressure: '',
    pulse: '',
    respirations: '',
    temperature: '',
    height: '',
    weight: '',
    weight_change: 'static',
    
    // Medical
    conditions: [],
    medications: [],
    allergies: [],
    surgeries: [],
    
    // Senses
    wears_glasses: false,
    vision_impaired: false,
    uses_hearing_aid: false,
    hearing_impaired: false,
    has_dentures: false,
    dental_problems: false,
    
    // Mental
    oriented_person: true,
    oriented_place: true,
    oriented_time: true,
    memory_intact: true,
    cooperative: true,
    anxious: false,
    depressed: false,
    
    // ADL
    adl_levels: {},
    
    // IADL
    iadl_levels: {},
    
    // Summary
    attendant_needed: true,
    attendant_frequency: 'intermittent',
    additional_notes: '',
  });

  useEffect(() => {
    fetchRequest();
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setRequest(data);
        // Pre-fill client info from service request
        setFormData(prev => ({
          ...prev,
          client_name: data.client_name || '',
          client_phone: data.client_phone || '',
          client_address: data.client_address || '',
        }));
      } else {
        toast.error('Service request not found');
        navigate('/organization/data-entry');
      }
    } catch (err) {
      console.error('Error fetching request:', err);
      toast.error('Failed to load service request');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateADLLevel = (key, level) => {
    setFormData(prev => ({
      ...prev,
      adl_levels: { ...prev.adl_levels, [key]: level }
    }));
  };

  const updateIADLLevel = (key, level) => {
    setFormData(prev => ({
      ...prev,
      iadl_levels: { ...prev.iadl_levels, [key]: level }
    }));
  };

  const addToList = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const removeFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // ============ VOICE RECORDING ============
  
  const startFullVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/wav';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await transcribeFullAssessment(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsFullVoiceMode(true);
      setRecordingDuration(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      toast.info('Recording started. Speak your assessment observations...');
    } catch (err) {
      console.error('Error starting recording:', err);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopFullVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      toast.info('Processing recording...');
    }
  };

  const transcribeFullAssessment = async (audioBlob) => {
    setIsExtracting(true);
    try {
      // Step 1: Transcribe the audio
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'assessment.webm');
      
      const transcribeRes = await fetch(`${API_URL}/api/data-entry/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!transcribeRes.ok) {
        throw new Error('Transcription failed');
      }
      
      const transcribeData = await transcribeRes.json();
      const transcription = transcribeData.raw_transcription || transcribeData.processed_value;
      setVoiceTranscription(transcription);
      
      // Step 2: Extract structured data using AI
      const extractRes = await fetch(
        `${API_URL}/api/intake/assessments/extract-from-voice?transcription=${encodeURIComponent(transcription)}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (!extractRes.ok) {
        throw new Error('AI extraction failed');
      }
      
      const extractData = await extractRes.json();
      
      if (extractData.success && extractData.extracted_data) {
        applyExtractedData(extractData.extracted_data);
        setExtractionComplete(true);
        toast.success('Assessment data extracted! Review and adjust as needed.');
      }
    } catch (err) {
      console.error('Voice assessment error:', err);
      toast.error('Failed to process voice assessment. Try again or enter manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  const applyExtractedData = (data) => {
    setFormData(prev => {
      const updated = { ...prev };
      
      // Demographics
      if (data.demographics) {
        const d = data.demographics;
        if (d.client_name) updated.client_name = d.client_name;
        if (d.date_of_birth) updated.date_of_birth = d.date_of_birth;
        if (d.client_phone) updated.client_phone = d.client_phone;
        if (d.client_address) updated.client_address = d.client_address;
        if (d.doctor_name) updated.doctor_name = d.doctor_name;
        if (d.doctor_phone) updated.doctor_phone = d.doctor_phone;
        if (d.emergency_contact_name) updated.emergency_contact_name = d.emergency_contact_name;
        if (d.emergency_contact_phone) updated.emergency_contact_phone = d.emergency_contact_phone;
      }
      
      // Vital Signs
      if (data.vital_signs) {
        const v = data.vital_signs;
        if (v.blood_pressure) updated.blood_pressure = v.blood_pressure;
        if (v.pulse) updated.pulse = String(v.pulse);
        if (v.respirations) updated.respirations = String(v.respirations);
        if (v.temperature) updated.temperature = String(v.temperature);
        if (v.height) updated.height = v.height;
        if (v.weight) updated.weight = v.weight;
        if (v.weight_change) updated.weight_change = v.weight_change;
      }
      
      // Medical
      if (data.medical_info) {
        const m = data.medical_info;
        if (m.conditions?.length) updated.conditions = m.conditions;
        if (m.medications?.length) updated.medications = m.medications;
        if (m.allergies?.length) updated.allergies = m.allergies;
        if (m.surgeries?.length) updated.surgeries = m.surgeries;
      }
      
      // Senses
      if (data.senses) {
        const s = data.senses;
        if (s.wears_glasses !== undefined) updated.wears_glasses = s.wears_glasses;
        if (s.vision_impaired !== undefined) updated.vision_impaired = s.vision_impaired;
        if (s.uses_hearing_aid !== undefined) updated.uses_hearing_aid = s.uses_hearing_aid;
        if (s.hearing_impaired !== undefined) updated.hearing_impaired = s.hearing_impaired;
        if (s.has_dentures !== undefined) updated.has_dentures = s.has_dentures;
        if (s.dental_problems !== undefined) updated.dental_problems = s.dental_problems;
      }
      
      // Mental
      if (data.mental_status) {
        const ms = data.mental_status;
        if (ms.oriented_person !== undefined) updated.oriented_person = ms.oriented_person;
        if (ms.oriented_place !== undefined) updated.oriented_place = ms.oriented_place;
        if (ms.oriented_time !== undefined) updated.oriented_time = ms.oriented_time;
        if (ms.memory_intact !== undefined) updated.memory_intact = ms.memory_intact;
        if (ms.cooperative !== undefined) updated.cooperative = ms.cooperative;
        if (ms.anxious !== undefined) updated.anxious = ms.anxious;
        if (ms.depressed !== undefined) updated.depressed = ms.depressed;
      }
      
      // ADL
      if (data.adl_assessment) {
        const adl = data.adl_assessment;
        const levels = {};
        Object.entries(adl).forEach(([key, val]) => {
          if (val) levels[key] = val;
        });
        if (Object.keys(levels).length) updated.adl_levels = levels;
      }
      
      // IADL
      if (data.iadl_assessment) {
        const iadl = data.iadl_assessment;
        const levels = {};
        Object.entries(iadl).forEach(([key, val]) => {
          if (val) levels[key] = val;
        });
        if (Object.keys(levels).length) updated.iadl_levels = levels;
      }
      
      // Care recommendation
      if (data.care_recommendation) {
        const cr = data.care_recommendation;
        if (cr.needs_attendant !== undefined) updated.attendant_needed = cr.needs_attendant;
        if (cr.attendant_frequency) updated.attendant_frequency = cr.attendant_frequency;
        if (cr.additional_notes) updated.additional_notes = cr.additional_notes;
      }
      
      return updated;
    });
  };

  // Field-by-field voice input
  const startFieldRecording = async (fieldName) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/wav';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await transcribeField(audioBlob, fieldName);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setActiveVoiceField(fieldName);
    } catch (err) {
      console.error('Error starting field recording:', err);
      toast.error('Could not access microphone');
    }
  };

  const stopFieldRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setActiveVoiceField(null);
    }
  };

  const transcribeField = async (audioBlob, fieldName) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'field.webm');
      formDataUpload.append('field_name', fieldName);
      
      const res = await fetch(`${API_URL}/api/data-entry/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      
      if (res.ok) {
        const result = await res.json();
        updateField(fieldName, result.processed_value || result.raw_transcription);
        toast.success('Voice transcribed');
      }
    } catch (err) {
      console.error('Field transcription error:', err);
      toast.error('Failed to transcribe');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============ FORM SUBMISSION ============

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/assessments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildAssessmentPayload())
      });

      if (res.ok) {
        toast.success('Assessment saved as draft');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save assessment');
      }
    } catch (err) {
      console.error('Error saving assessment:', err);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/assessments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildAssessmentPayload())
      });

      if (res.ok) {
        toast.success('Assessment completed! Proceeding to Care Plan...');
        navigate(`/intake/care-plan/${requestId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to complete assessment');
      }
    } catch (err) {
      console.error('Error completing assessment:', err);
      toast.error('Failed to complete assessment');
    } finally {
      setSaving(false);
    }
  };

  const buildAssessmentPayload = () => ({
    client_id: request?.client_id || requestId,
    service_request_id: requestId,
    assessment_date: new Date().toISOString().split('T')[0],
    client_name: formData.client_name,
    client_phone: formData.client_phone,
    client_address: formData.client_address,
    doctor_name: formData.doctor_name,
    doctor_phone: formData.doctor_phone,
    emergency_contact_name: formData.emergency_contact_name,
    emergency_contact_phone: formData.emergency_contact_phone,
    vital_signs: {
      blood_pressure: formData.blood_pressure,
      pulse: formData.pulse ? parseInt(formData.pulse) : null,
      respirations: formData.respirations ? parseInt(formData.respirations) : null,
      temperature: formData.temperature ? parseFloat(formData.temperature) : null,
      height: formData.height,
      weight: formData.weight,
      weight_change: formData.weight_change,
    },
    medical_info: {
      conditions: formData.conditions,
      medications: formData.medications,
      medication_allergies: formData.allergies,
      surgeries: formData.surgeries,
    },
    dental_vision_hearing: {
      wears_glasses: formData.wears_glasses,
      vision_unimpaired: !formData.vision_impaired,
      uses_hearing_aid: formData.uses_hearing_aid,
      hearing_unimpaired: !formData.hearing_impaired,
      has_dentures: formData.has_dentures,
      has_dental_problems: formData.dental_problems,
    },
    mental_assessment: {
      cooperative: formData.cooperative,
      anxious: formData.anxious,
      depressed: formData.depressed,
    },
    adl_assessment: formData.adl_levels,
    iadl_assessment: formData.iadl_levels,
    needs_attendant: formData.attendant_needed,
    attendant_frequency: formData.attendant_frequency,
    additional_notes: formData.additional_notes,
  });

  // ============ RENDER SECTIONS ============

  const renderVoiceModeSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Voice Assessment Mode"
        description="Record your observations during the home visit. AI will automatically extract and populate all form fields."
      />
      
      <div className="bg-gradient-to-br from-[#176DFF]/5 to-[#29C0FF]/5 rounded-2xl border-2 border-dashed border-[#176DFF]/30 p-6 md:p-8 text-center">
        {!isRecording && !isExtracting && !extractionComplete && (
          <>
            <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-[#176DFF] rounded-full flex items-center justify-center shadow-lg shadow-[#176DFF]/30">
              <Mic className="w-10 h-10 md:w-12 md:h-12 text-white" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#0D1B33] mb-2">Start Voice Assessment</h3>
            <p className="text-[#64748B] mb-6 max-w-md mx-auto text-sm md:text-base">
              Tap the button below and speak your observations. Describe the client&apos;s condition, vital signs, ADL abilities, and any concerns.
            </p>
            <Button
              size="lg"
              onClick={startFullVoiceRecording}
              className="bg-[#176DFF] hover:bg-[#176DFF]/90 text-lg px-8 py-6 h-auto"
            >
              <Mic className="w-6 h-6 mr-3" />
              Start Recording
            </Button>
            <p className="text-xs text-[#94A3B8] mt-4">
              Tip: Speak naturally, like you&apos;re dictating to a colleague
            </p>
          </>
        )}

        {isRecording && (
          <>
            <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/30">
              <Volume2 className="w-10 h-10 md:w-12 md:h-12 text-white" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#0D1B33] mb-2">Recording...</h3>
            <p className="text-3xl font-mono text-[#176DFF] mb-6">{formatDuration(recordingDuration)}</p>
            <Button
              size="lg"
              onClick={stopFullVoiceRecording}
              className="bg-red-500 hover:bg-red-600 text-lg px-8 py-6 h-auto"
            >
              <Square className="w-6 h-6 mr-3" />
              Stop Recording
            </Button>
            <p className="text-sm text-[#64748B] mt-4">
              Recording... Speak your observations
            </p>
          </>
        )}

        {isExtracting && (
          <>
            <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-[#176DFF] rounded-full flex items-center justify-center">
              <Sparkles className="w-10 h-10 md:w-12 md:h-12 text-white animate-pulse" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#0D1B33] mb-2">Processing with Leynix AI...</h3>
            <p className="text-[#64748B] mb-4">Transcribing audio and extracting assessment data</p>
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#176DFF]" />
          </>
        )}

        {extractionComplete && (
          <>
            <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-emerald-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 md:w-12 md:h-12 text-white" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#0D1B33] mb-2">Extraction Complete!</h3>
            <p className="text-[#64748B] mb-6 max-w-md mx-auto">
              AI has populated the form fields. Review each section and make adjustments as needed.
            </p>
            <div className="flex flex-col md:flex-row gap-3 justify-center">
              <Button
                onClick={() => setCurrentSection(1)}
                className="bg-[#176DFF] hover:bg-[#176DFF]/90"
              >
                Review Demographics
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExtractionComplete(false);
                  setVoiceTranscription('');
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Record Again
              </Button>
            </div>
          </>
        )}
      </div>

      {voiceTranscription && (
        <div className="bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] p-4">
          <h4 className="text-sm font-medium text-[#0D1B33] mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Transcription
          </h4>
          <p className="text-sm text-[#64748B] whitespace-pre-wrap">{voiceTranscription}</p>
        </div>
      )}

      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">Tips for Best Results</h4>
            <ul className="text-sm text-amber-700 mt-2 space-y-1">
              <li>• Speak clearly and at a normal pace</li>
              <li>• State numbers explicitly: &quot;blood pressure one twenty over eighty&quot;</li>
              <li>• Use terms like &quot;needs moderate assistance with bathing&quot;</li>
              <li>• Mention any concerns or special observations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDemographicsSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Client Demographics"
        description="Basic client information and emergency contacts"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VoiceField label="Client Name" required field="client_name" value={formData.client_name} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'client_name'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Date of Birth" field="date_of_birth" value={formData.date_of_birth} onChange={updateField} type="date" />
        <VoiceField label="Phone" field="client_phone" value={formData.client_phone} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'client_phone'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Address" field="client_address" value={formData.client_address} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'client_address'} onStopVoice={stopFieldRecording} />
      </div>
      
      <div className="border-t border-[#E2E8F0] pt-6">
        <h3 className="font-medium text-[#0D1B33] mb-4">Physician Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VoiceField label="Doctor Name" field="doctor_name" value={formData.doctor_name} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'doctor_name'} onStopVoice={stopFieldRecording} />
          <VoiceField label="Doctor Phone" field="doctor_phone" value={formData.doctor_phone} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'doctor_phone'} onStopVoice={stopFieldRecording} />
        </div>
      </div>
      
      <div className="border-t border-[#E2E8F0] pt-6">
        <h3 className="font-medium text-[#0D1B33] mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VoiceField label="Contact Name" field="emergency_contact_name" value={formData.emergency_contact_name} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'emergency_contact_name'} onStopVoice={stopFieldRecording} />
          <VoiceField label="Contact Phone" field="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={updateField} onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'emergency_contact_phone'} onStopVoice={stopFieldRecording} />
        </div>
      </div>
    </div>
  );

  const renderVitalSignsSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Vital Signs"
        description="Current vital signs and physical measurements"
      />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <VoiceField label="Blood Pressure" field="blood_pressure" value={formData.blood_pressure} onChange={updateField} placeholder="120/80" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'blood_pressure'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Pulse (bpm)" field="pulse" value={formData.pulse} onChange={updateField} placeholder="72" type="number" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'pulse'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Respirations" field="respirations" value={formData.respirations} onChange={updateField} placeholder="16" type="number" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'respirations'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Temperature (°F)" field="temperature" value={formData.temperature} onChange={updateField} placeholder="98.6" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'temperature'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Height" field="height" value={formData.height} onChange={updateField} placeholder="5 ft 8 in" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'height'} onStopVoice={stopFieldRecording} />
        <VoiceField label="Weight" field="weight" value={formData.weight} onChange={updateField} placeholder="165 lbs" onVoice={startFieldRecording} isRecording={isRecording && activeVoiceField === 'weight'} onStopVoice={stopFieldRecording} />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#0D1B33] mb-2">Weight Change</label>
        <div className="flex flex-wrap gap-2 md:gap-4">
          {['increase', 'static', 'decrease'].map((option) => (
            <button
              key={option}
              onClick={() => updateField('weight_change', option)}
              className={`flex-1 min-w-[100px] py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                formData.weight_change === option
                  ? 'bg-[#176DFF] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMedicalSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Medical History"
        description="Conditions, medications, and allergies"
      />
      
      <ListInput
        label="Medical Conditions"
        items={formData.conditions}
        onAdd={(v) => addToList('conditions', v)}
        onRemove={(i) => removeFromList('conditions', i)}
        placeholder="e.g., Diabetes, Hypertension"
      />
      
      <ListInput
        label="Current Medications"
        items={formData.medications}
        onAdd={(v) => addToList('medications', v)}
        onRemove={(i) => removeFromList('medications', i)}
        placeholder="e.g., Metformin 500mg twice daily"
      />
      
      <ListInput
        label="Allergies"
        items={formData.allergies}
        onAdd={(v) => addToList('allergies', v)}
        onRemove={(i) => removeFromList('allergies', i)}
        placeholder="e.g., Penicillin, Sulfa"
      />
      
      <ListInput
        label="Surgeries"
        items={formData.surgeries}
        onAdd={(v) => addToList('surgeries', v)}
        onRemove={(i) => removeFromList('surgeries', i)}
        placeholder="e.g., Hip replacement 2020"
      />
    </div>
  );

  const renderSensesSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Vision, Hearing & Dental"
        description="Sensory status assessment"
      />
      
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-[#F8FAFC] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-[#176DFF]" />
            <h3 className="font-medium text-[#0D1B33]">Vision</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <MobileCheckbox label="Wears glasses/contacts" checked={formData.wears_glasses} onChange={(v) => updateField('wears_glasses', v)} />
            <MobileCheckbox label="Vision impaired" checked={formData.vision_impaired} onChange={(v) => updateField('vision_impaired', v)} />
          </div>
        </div>
        
        <div className="bg-[#F8FAFC] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Ear className="w-5 h-5 text-[#176DFF]" />
            <h3 className="font-medium text-[#0D1B33]">Hearing</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <MobileCheckbox label="Uses hearing aid" checked={formData.uses_hearing_aid} onChange={(v) => updateField('uses_hearing_aid', v)} />
            <MobileCheckbox label="Hearing impaired" checked={formData.hearing_impaired} onChange={(v) => updateField('hearing_impaired', v)} />
          </div>
        </div>
        
        <div className="bg-[#F8FAFC] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-[#176DFF]" />
            <h3 className="font-medium text-[#0D1B33]">Dental</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <MobileCheckbox label="Has dentures" checked={formData.has_dentures} onChange={(v) => updateField('has_dentures', v)} />
            <MobileCheckbox label="Dental problems" checked={formData.dental_problems} onChange={(v) => updateField('dental_problems', v)} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderMentalSection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Mental/Cognitive Status"
        description="Orientation and behavioral assessment"
      />
      
      <div className="bg-[#F8FAFC] rounded-xl p-4">
        <h3 className="font-medium text-[#0D1B33] mb-4">Orientation</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MobileCheckbox label="Person" checked={formData.oriented_person} onChange={(v) => updateField('oriented_person', v)} />
          <MobileCheckbox label="Place" checked={formData.oriented_place} onChange={(v) => updateField('oriented_place', v)} />
          <MobileCheckbox label="Time" checked={formData.oriented_time} onChange={(v) => updateField('oriented_time', v)} />
          <MobileCheckbox label="Memory Intact" checked={formData.memory_intact} onChange={(v) => updateField('memory_intact', v)} />
        </div>
      </div>
      
      <div className="bg-[#F8FAFC] rounded-xl p-4">
        <h3 className="font-medium text-[#0D1B33] mb-4">Behavior/Mood</h3>
        <div className="grid grid-cols-3 gap-3">
          <MobileCheckbox label="Cooperative" checked={formData.cooperative} onChange={(v) => updateField('cooperative', v)} />
          <MobileCheckbox label="Anxious" checked={formData.anxious} onChange={(v) => updateField('anxious', v)} />
          <MobileCheckbox label="Depressed" checked={formData.depressed} onChange={(v) => updateField('depressed', v)} />
        </div>
      </div>
    </div>
  );

  const renderADLSection = () => (
    <div className="space-y-4">
      <SectionHeader
        title="Activities of Daily Living (ADL)"
        description="Assess the client's ability to perform basic self-care activities"
      />
      
      <div className="space-y-3">
        {ADL_ITEMS.map((item) => (
          <ADLIADLCard
            key={item.key}
            item={item}
            level={formData.adl_levels[item.key]}
            onLevelChange={(level) => updateADLLevel(item.key, level)}
          />
        ))}
      </div>
    </div>
  );

  const renderIADLSection = () => (
    <div className="space-y-4">
      <SectionHeader
        title="Instrumental Activities of Daily Living (IADL)"
        description="Assess the client's ability to perform complex daily tasks"
      />
      
      <div className="space-y-3">
        {IADL_ITEMS.map((item) => (
          <ADLIADLCard
            key={item.key}
            item={item}
            level={formData.iadl_levels[item.key]}
            onLevelChange={(level) => updateIADLLevel(item.key, level)}
          />
        ))}
      </div>
    </div>
  );

  const renderSummarySection = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Assessment Summary"
        description="Care recommendations and additional notes"
      />
      
      <div className="bg-[#F8FAFC] rounded-xl p-4">
        <h3 className="font-medium text-[#0D1B33] mb-4">Attendant Care Recommendation</h3>
        <div className="space-y-4">
          <MobileCheckbox label="Client needs attendant care" checked={formData.attendant_needed} onChange={(v) => updateField('attendant_needed', v)} />
          
          {formData.attendant_needed && (
            <div>
              <label className="block text-sm font-medium text-[#0D1B33] mb-2">Frequency</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { value: 'intermittent', label: 'Intermittent' },
                  { value: 'constant_day', label: 'Constant (Day)' },
                  { value: 'during_night', label: 'During Night' },
                  { value: '24_hour', label: '24-Hour' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateField('attendant_frequency', option.value)}
                    className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                      formData.attendant_frequency === option.value
                        ? 'bg-[#176DFF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#0D1B33] mb-2">
          Additional Notes
        </label>
        <div className="relative">
          <textarea
            value={formData.additional_notes}
            onChange={(e) => updateField('additional_notes', e.target.value)}
            placeholder="Any additional observations or recommendations..."
            className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] min-h-[120px]"
            rows={5}
          />
          <button
            onClick={() => isRecording && activeVoiceField === 'additional_notes' ? stopFieldRecording() : startFieldRecording('additional_notes')}
            className={`absolute right-3 top-3 p-2 rounded-lg transition-colors ${
              isRecording && activeVoiceField === 'additional_notes'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isRecording && activeVoiceField === 'additional_notes' ? (
              <MicOff className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
        <h3 className="font-medium text-emerald-900 mb-3">Assessment Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-emerald-700">ADLs:</span>
            <span className="font-medium text-emerald-900 ml-2">
              {Object.keys(formData.adl_levels).length}/{ADL_ITEMS.length}
            </span>
          </div>
          <div>
            <span className="text-emerald-700">IADLs:</span>
            <span className="font-medium text-emerald-900 ml-2">
              {Object.keys(formData.iadl_levels).length}/{IADL_ITEMS.length}
            </span>
          </div>
          <div>
            <span className="text-emerald-700">Conditions:</span>
            <span className="font-medium text-emerald-900 ml-2">{formData.conditions.length}</span>
          </div>
          <div>
            <span className="text-emerald-700">Medications:</span>
            <span className="font-medium text-emerald-900 ml-2">{formData.medications.length}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (currentSection) {
      case 0: return renderVoiceModeSection();
      case 1: return renderDemographicsSection();
      case 2: return renderVitalSignsSection();
      case 3: return renderMedicalSection();
      case 4: return renderSensesSection();
      case 5: return renderMentalSection();
      case 6: return renderADLSection();
      case 7: return renderIADLSection();
      case 8: return renderSummarySection();
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <header className="mb-4 md:mb-6">
          <button
            onClick={() => navigate('/organization/data-entry')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-3 md:mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-semibold text-[#0D1B33]">Client Assessment</h1>
              <p className="text-xs md:text-sm text-[#64748B]">
                Step 3 of 6 — {request?.client_name}
              </p>
            </div>
          </div>
        </header>

        {/* Section Navigation - Mobile optimized */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-2 md:p-4 mb-4 md:mb-6 overflow-x-auto sticky top-0 z-10">
          <div className="flex gap-1 md:gap-2 min-w-max">
            {SECTIONS.map((section, idx) => {
              const Icon = section.icon;
              const isActive = currentSection === idx;
              const isComplete = idx < currentSection && idx !== 0;
              
              return (
                <button
                  key={section.key}
                  onClick={() => setCurrentSection(idx)}
                  className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-[#176DFF] text-white'
                      : isComplete
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {isComplete && !isActive ? (
                    <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  ) : (
                    <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  )}
                  <span className="hidden md:inline">{section.label}</span>
                  <span className="md:hidden">{section.mobileLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 md:p-6 mb-4 md:mb-6">
          {renderSection()}
        </div>

        {/* Navigation - Mobile optimized */}
        <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
          <Button
            variant="outline"
            onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : navigate('/organization/data-entry')}
            className="order-2 md:order-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {currentSection > 0 ? 'Previous' : 'Cancel'}
          </Button>

          <div className="flex gap-2 md:gap-3 order-1 md:order-2">
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving} className="flex-1 md:flex-none">
              <Save className="w-4 h-4 mr-1" />
              Save Draft
            </Button>

            {currentSection < SECTIONS.length - 1 ? (
              <Button
                className="bg-[#176DFF] hover:bg-[#176DFF]/90 flex-1 md:flex-none"
                onClick={() => setCurrentSection(currentSection + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 flex-1 md:flex-none"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Complete
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ HELPER COMPONENTS ============

function SectionHeader({ title, description }) {
  return (
    <div className="mb-4 md:mb-6">
      <h2 className="text-lg md:text-xl font-semibold text-[#0D1B33]">{title}</h2>
      {description && <p className="text-sm text-[#64748B] mt-1">{description}</p>}
    </div>
  );
}

function VoiceField({ label, field, value, onChange, type = 'text', placeholder, required, onVoice, isRecording, onStopVoice }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0D1B33] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          placeholder={placeholder}
          className={`${onVoice ? 'pr-12' : ''} h-12`}
        />
        {onVoice && (
          <button
            type="button"
            onClick={() => isRecording ? onStopVoice() : onVoice(field)}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
              isRecording
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function MobileCheckbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer p-2 -m-2 rounded-lg hover:bg-gray-50 transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
      />
      <span className="text-sm text-[#0D1B33]">{label}</span>
    </label>
  );
}

function ListInput({ label, items, onAdd, onRemove, placeholder }) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-[#0D1B33] mb-2">{label}</label>
      <div className="flex gap-2 mb-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          placeholder={placeholder}
          className="flex-1 h-12"
        />
        <Button type="button" variant="outline" onClick={handleAdd} className="h-12 px-4">Add</Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#176DFF]/10 text-[#176DFF] rounded-full text-sm"
            >
              {item}
              <button
                onClick={() => onRemove(idx)}
                className="ml-1 hover:text-red-600 text-lg leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ADLIADLCard({ item, level, onLevelChange }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1">
          <h4 className="font-medium text-[#0D1B33]">{item.label}</h4>
          <p className="text-xs text-[#64748B] mt-0.5">{item.description}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ASSISTANCE_LEVELS.map((lvl) => (
            <button
              key={lvl.value}
              onClick={() => onLevelChange(lvl.value)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                level === lvl.value
                  ? lvl.color
                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <span className="hidden md:inline">{lvl.label}</span>
              <span className="md:hidden">{lvl.short}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
